import { useTranslation } from "react-i18next";
import { Check, UserCheck, X } from "lucide-react";

import { useSessionAttendance } from "../../../application/useSessionAttendance";
import { attendanceSummary } from "../../../domain/sessionExtras";

interface SessionAttendanceProps {
    sessionId: string;
}

const TOGGLE_BASE =
    "inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";
const TOGGLE_OFF = "border-gray-200 text-content-tertiary hover:border-gray-300 dark:border-white/10";
const PRESENT_ON = "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300";
const ABSENT_ON = "border-gray-500 bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-100";

/** Who of the group came: each member marked present or absent, one tap each. */
export default function SessionAttendance({ sessionId }: SessionAttendanceProps) {
    const { t } = useTranslation();
    const { entries, loading, loadFailed, saveFailed, savingId, mark } = useSessionAttendance(sessionId);
    const summary = attendanceSummary(entries);

    return (
        <section
            aria-labelledby="session-attendance-title"
            className="glass-effect glass-border border rounded-2xl p-6 shadow-theme-sm"
        >
            <div className="flex items-center justify-between gap-3 mb-4">
                <h3
                    id="session-attendance-title"
                    className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2"
                >
                    <UserCheck className="w-4 h-4 text-brand-500" aria-hidden="true" />
                    {t("session.attendance.title")}
                </h3>
                {!loading && !loadFailed && entries.length > 0 && (
                    <span className="text-xs font-medium text-content-tertiary">
                        {t("session.attendance.summary", { attended: summary.attended, total: summary.total })}
                    </span>
                )}
            </div>

            {loading ? (
                <p className="text-sm text-content-tertiary">{t("common.loading")}</p>
            ) : loadFailed ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("session.attendance.loadFailed")}
                </p>
            ) : (
                <ul className="divide-y divide-gray-100 dark:divide-white/5">
                    {entries.map((entry) => {
                        const name = `${entry.firstName} ${entry.lastName}`.trim();
                        const busy = savingId === entry.patientId;
                        return (
                            <li key={entry.patientId} className="flex items-center justify-between gap-3 py-2.5">
                                <span className="text-sm text-content-primary truncate">{name}</span>
                                <div role="group" aria-label={name} className="flex shrink-0 gap-1.5">
                                    <button
                                        type="button"
                                        aria-pressed={entry.attended === true}
                                        disabled={busy}
                                        onClick={() => mark(entry.patientId, true)}
                                        className={`${TOGGLE_BASE} ${entry.attended === true ? PRESENT_ON : TOGGLE_OFF}`}
                                    >
                                        <Check className="w-3.5 h-3.5" aria-hidden="true" />
                                        {t("session.attendance.present")}
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={entry.attended === false}
                                        disabled={busy}
                                        onClick={() => mark(entry.patientId, false)}
                                        className={`${TOGGLE_BASE} ${entry.attended === false ? ABSENT_ON : TOGGLE_OFF}`}
                                    >
                                        <X className="w-3.5 h-3.5" aria-hidden="true" />
                                        {t("session.attendance.absent")}
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {saveFailed && (
                <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                    {t("session.attendance.saveFailed")}
                </p>
            )}
        </section>
    );
}
